import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {adminLogout, adminRefreshSessionIfNeeded, getAdminSession, type AdminUser} from "@zaaaz/shared";
import {useActivityRefresh} from "@/hooks/useActivityRefresh";

type AdminAuthContextValue = {
  user: AdminUser | null;
  ready: boolean;
  refresh: () => Promise<AdminUser>;
  applySession: (user: AdminUser) => void;
  logout: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({children}: {children: React.ReactNode}) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [ready, setReady] = useState(false);
  const applySession = useCallback((u: AdminUser) => {
    setUser(u);
  }, []);
  const refresh = useCallback(async () => {
    const u = await getAdminSession();
    setUser(u);
    return u;
  }, []);
  const extendSession = useCallback(() => {
    adminRefreshSessionIfNeeded().then((u) => {
      if(u?.authenticated) setUser(u);
    }).catch(() => {});
  }, []);
  const logout = useCallback(async () => {
    await adminLogout();
    setUser({authenticated: false});
  }, []);
  useEffect(() => {
    refresh().finally(() => setReady(true));
  }, [refresh]);
  useActivityRefresh(extendSession, !!user?.authenticated);
  useEffect(() => {
    if(!user?.authenticated) return;
    const t = setInterval(extendSession, 12 * 60 * 60 * 1000);
    return () => clearInterval(t);
  }, [user?.authenticated, extendSession]);
  const value = useMemo(() => ({user, ready, refresh, applySession, logout}), [user, ready, refresh, applySession, logout]);
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if(!ctx) throw new Error("useAdminAuth outside AdminAuthProvider");
  return ctx;
}
